import { UnsupportedValueError, warnUnsupportedValue } from "@/lib/error.ts";
import { cn, getProjectId } from "@/lib/utils.ts";
import type {
  ActionThreadEvent,
  CompactionThreadEvent,
  DebugThreadEvent,
  InferenceThreadEvent,
  ObservationThreadEvent,
  ResourceThreadEvent,
} from "@/types/threadEvents.ts";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import { useAtomValue } from "jotai";
import {
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Circle,
  CircleHelp,
  Copy,
  Loader2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Markdown } from "../markdown/Markdown.tsx";
import { Badge } from "../ui/badge.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible.tsx";
import { confirmationToast } from "../ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";
import { type AutoExpandMode, autoExpandAtom } from "./Omnibox/DebugLinkButton.tsx";
import { buildTurnSegments, childClusterItemKey } from "@/shared/eventTree.ts";
import type {
  ChildClusterItem,
  ChildClusterRootItem,
  NonMessageThreadEvent,
  OrphanRootItem,
  TurnNode,
  TurnRootItem,
} from "@/shared/eventTree.ts";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const HighlightedCode = ({ code, language }: { code: string; language: string }) => {
  const highlightedHtml = useMemo(() => {
    const lang = language.toLowerCase();

    // Only highlight registered languages. Don't use highlightAuto if this doesn't return
    // a language - that tries a bunch of grammars against the code block to try to guess the language,
    // which is expensive. We render a lot of Forthic/plaintext, which we know will never match, so just bail out.
    if (!hljs.getLanguage(lang)) return null;
    return hljs.highlight(code, { language: lang }).value;
  }, [code, language]);

  if (highlightedHtml === null) return code;

  return (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: highlight.js output from our own debug data
    <code dangerouslySetInnerHTML={{ __html: highlightedHtml }} />
  );
};

const CodeBlock = React.memo(function CodeBlock({
  code,
  language,
  label,
  suffix,
  copyText,
}: {
  code: string;
  language: string;
  label?: string;
  suffix?: React.ReactNode;
  copyText?: string;
}) {
  const copyLabel = `Copy ${label ?? language}`;
  return (
    <div className="rounded border bg-popover text-xs font-mono overflow-hidden">
      <div className="px-2.5 py-0.5 text-2xs text-muted-foreground bg-muted/30 flex items-center gap-1">
        <span>{label ?? language}</span>
        {suffix}
        <span className="ml-auto -mr-1.5">
          <CopyButton getText={() => copyText ?? code} label={copyLabel} />
        </span>
      </div>
      <pre className="px-2.5 py-1.5 whitespace-pre-wrap hljs max-h-48 overflow-y-auto">
        <HighlightedCode code={code} language={language} />
      </pre>
    </div>
  );
});

const Timestamp = ({ iso, className }: { iso: string; className?: string }) => {
  const date = new Date(iso);
  return (
    <span
      className={cn(
        "text-2xs text-muted-foreground font-mono tabular-nums whitespace-nowrap",
        className,
      )}
    >
      {date.toLocaleTimeString(undefined, { hour12: false })}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Shared event card container + header
// ---------------------------------------------------------------------------

const EventCard = ({
  borderClassName,
  header,
  defaultOpen = true,
  children,
}: {
  borderClassName: string;
  /**
   * Must internally render a `<CollapsibleTrigger>` over the clickable area
   * (see `EventCardHeader`). EventCard intentionally doesn't wrap the whole
   * header in a trigger anymore so sibling controls (e.g. the TimingBreakdown
   * tooltip button) can sit alongside without nesting buttons.
   */
  header: React.ReactNode;
  defaultOpen?: boolean;
  children?: React.ReactNode;
}) => {
  const hasBody = !!children;
  return (
    <Collapsible defaultOpen={hasBody ? defaultOpen : false} disabled={!hasBody}>
      <div
        className={cn(
          "border rounded-lg bg-popover text-popover-foreground overflow-hidden px-2.5 py-1.5",
          borderClassName,
        )}
      >
        {header}
        {hasBody && (
          <CollapsibleContent>
            <div className="mt-1.5">{children}</div>
          </CollapsibleContent>
        )}
      </div>
    </Collapsible>
  );
};

const formatDurationMs = (ms: number): string =>
  ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;

// Only the chevron+label area is the collapse trigger so the optional
// `extraTiming` control (a Radix TooltipTrigger button) can sit alongside it as
// a sibling instead of nested inside another `<button>`.
const EventCardHeader = ({
  label,
  labelClassName,
  chevronClassName,
  durationMs,
  extraTiming,
  sortDate,
  hasBody = true,
  isCommitted,
}: {
  label: React.ReactNode;
  labelClassName: string;
  chevronClassName?: string;
  durationMs?: number;
  /** Extra timing control rendered between the duration and the timestamp. Must not itself be a button-in-button. */
  extraTiming?: React.ReactNode;
  sortDate: string;
  hasBody?: boolean;
  isCommitted: boolean;
}) => (
  <div className="flex items-start gap-2 w-full">
    <CollapsibleTrigger
      disabled={!hasBody}
      className={cn(
        "CollapsibleTrigger flex-1 min-w-0 flex items-start gap-2 text-left",
        hasBody && "cursor-pointer",
      )}
    >
      {hasBody ? (
        <ChevronDown
          className={cn(
            "size-3 shrink-0 transition-transform CollapsibleTriggerRightIcon mt-0.5",
            chevronClassName ?? labelClassName,
          )}
        />
      ) : (
        <Circle
          className={cn(
            // mx- to compensate visually for it being smaller than the chevron
            "size-2 shrink-0 mt-1 mx-0.5 stroke-[2.5]",
            chevronClassName ?? labelClassName,
          )}
        />
      )}
      <span className={cn("font-semibold font-mono text-2xs text-left", labelClassName)}>
        {label}
      </span>
    </CollapsibleTrigger>
    <span className="flex items-center gap-1.5 text-2xs text-muted-foreground font-mono tabular-nums">
      {!isCommitted && <Loader2 className="size-3 animate-spin" />}
      {durationMs !== undefined && (
        <span className="inline-flex items-center gap-1">{formatDurationMs(durationMs)}</span>
      )}
      {extraTiming}
      {durationMs !== undefined && <span aria-hidden>·</span>}
      <Timestamp iso={sortDate} />
    </span>
  </div>
);

const ACTION_KIND_LABELS: Record<ActionThreadEvent["content"]["kind"], string> = {
  execution: "Exec",
  agent: "Agent",
  workflowStep: "Workflow Step",
  injected: "Injected",
};

type InferenceUsage = NonNullable<InferenceThreadEvent["content"]["meta"]>["usage"];

type InferenceInput = NonNullable<InferenceThreadEvent["content"]["input"]>;

type FullPrompt = NonNullable<InferenceThreadEvent["content"]["fullPrompt"]>;

const DEFAULT_OPEN_SECTIONS = new Set(["actionLog", "guidanceLog", "query"]);

/**
 * Inference input fields whose `value` is a serialized JSON object — render
 * them with JSON syntax highlighting. Other fields (eventLog, currentDateTime,
 * actionLog, etc.) are plain strings.
 */
const JSON_INPUT_FIELDS = new Set(["userProfiles", "generalContext", "interpreterState"]);

// Shared styles for rows inside an inference card (top-level sections and
// their nested labeled sub-sections / output fields).
const ROW_CLS = "flex items-center gap-1.5 py-0.5 text-2xs text-muted-foreground w-full";
const TRIGGER_CLS = cn(
  ROW_CLS,
  "CollapsibleTrigger hover:text-foreground transition-colors cursor-pointer",
);
const CHEVRON_BASE_CLS = "shrink-0 transition-transform CollapsibleTriggerRightIcon";
const TRIGGER_LABEL_CLS = "font-mono font-semibold";
const COUNT_SUFFIX_CLS = "text-muted-foreground/60 ml-1 tabular-nums font-medium";
const COLLAPSIBLE_FIELD_CONTENT_CLS =
  "whitespace-pre-wrap text-2xs text-muted-foreground font-mono overflow-y-auto p-1.5 rounded bg-muted/30 border border-border";

/**
 * Small inline copy-to-clipboard button. Stops event propagation so clicking
 * it inside a `CollapsibleTrigger` doesn't toggle the parent. Shows a
 * transient check icon for 1.2s after a successful copy.
 */
const CopyButton = ({
  getText,
  label,
  onCopied,
}: {
  getText: () => string;
  label: string;
  /** Fired after the text is successfully written to the clipboard. */
  onCopied?: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        const text = getText();
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          onCopied?.();
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      className="-ml-0.5 p-0.5 rounded hover:bg-muted/60 text-muted-foreground/60 hover:text-foreground transition-colors cursor-pointer"
    >
      {copied ? <Check className="size-2.5" /> : <Copy className="size-2.5" />}
    </button>
  );
};

const CountSuffix = ({ children }: { children: React.ReactNode }) => (
  <span className={COUNT_SUFFIX_CLS}>({children})</span>
);

/**
 * Inline toggle that expands or collapses every row in a section at once.
 * Labelled "Collapse All" only when every row is open; "Expand All" otherwise
 * (including the mixed some-open/some-closed state). Stops propagation so a
 * click doesn't toggle the parent collapsible it lives inside.
 */
const ExpandAllToggle = ({ allOpen, onToggle }: { allOpen: boolean; onToggle: () => void }) => (
  <button
    type="button"
    // No aria-label: the visible "Expand All" / "Collapse All" text is the
    // accessible name. `title` just adds a hover tooltip.
    title={allOpen ? "Collapse all" : "Expand all"}
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
      onToggle();
    }}
    className="flex items-center gap-1 p-0.5 rounded text-2xs font-medium text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
  >
    {allOpen ? <ChevronsDownUp className="size-2.5" /> : <ChevronsUpDown className="size-2.5" />}
    {allOpen ? "Collapse All" : "Expand All"}
  </button>
);

const SectionTrigger = ({
  level,
  label,
  suffix,
  copy,
  rightContent,
}: {
  level: "primary" | "secondary";
  label: string;
  suffix?: React.ReactNode;
  copy?: { getText: () => string; label: string };
  /** Optional content right-aligned at the end of the trigger row (after copy). */
  rightContent?: React.ReactNode;
}) => {
  const isPrimary = level === "primary";
  return (
    <CollapsibleTrigger className={TRIGGER_CLS}>
      <ChevronDown className={cn(CHEVRON_BASE_CLS, isPrimary ? "size-3" : "size-2.5")} />
      <span className={TRIGGER_LABEL_CLS}>{label}</span>
      {suffix}
      {copy && <CopyButton getText={copy.getText} label={copy.label} />}
      {rightContent && <span className="ml-auto">{rightContent}</span>}
    </CollapsibleTrigger>
  );
};

/**
 * Plain `<pre>` body used inside collapsible field rows. Pass `language` to
 * syntax-highlight a string child via `HighlightedCode`; non-string children
 * (or unrecognized languages) render as-is.
 */
const CollapsibleFieldContent = ({
  children,
  className,
  language,
}: {
  children: React.ReactNode;
  className?: string;
  language?: string;
}) => (
  <pre className={cn(COLLAPSIBLE_FIELD_CONTENT_CLS, language && "hljs", className)}>
    {language && typeof children === "string" ? (
      <HighlightedCode code={children} language={language} />
    ) : (
      children
    )}
  </pre>
);

/**
 * Secondary collapsible row: a labeled trigger that toggles a code body.
 * Used by inference inputs/outputs and the per-observation interpreter-state
 * diff. Pass `language` to syntax-highlight the body.
 */
const CollapsibleField = ({
  label,
  body,
  language,
  defaultOpen = false,
  open,
  onOpenChange,
  suffix,
  bodyMaxHeightClassName = "max-h-64",
}: {
  label: string;
  body: string;
  language?: string;
  defaultOpen?: boolean;
  // When `open` is provided the row is controlled (used by the full-prompt
  // expand/collapse-all toggle); otherwise it stays uncontrolled via defaultOpen.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  suffix?: React.ReactNode;
  bodyMaxHeightClassName?: string;
}) => (
  <Collapsible {...(open === undefined ? { defaultOpen } : { open, onOpenChange })}>
    <SectionTrigger
      level="secondary"
      label={label}
      suffix={suffix}
      copy={{ getText: () => body, label: `Copy ${label}` }}
    />
    <CollapsibleContent>
      <CollapsibleFieldContent
        className={cn(bodyMaxHeightClassName, "mt-0.5 ml-4")}
        language={language}
      >
        {body}
      </CollapsibleFieldContent>
    </CollapsibleContent>
  </Collapsible>
);

/** Rough token estimate from raw character length (~4 chars/token). */
const estTokens = (s: string): number => Math.round(s.length / 4);

const hasPromptTokenData = (usage?: InferenceUsage): boolean =>
  usage?.promptTokens !== undefined ||
  usage?.cacheReadTokens !== undefined ||
  usage?.cacheCreationTokens !== undefined;

/**
 * `<x> tok (<y> cr, <z> cw, <w> unc)` — `x` is the total prompt-token count
 * billed for the call, which is `cr + cw + unc`. Anthropic (and Ax's
 * normalized shape) reports `promptTokens` as the *new/uncached* input
 * tokens only — cache reads and writes are billed separately and live on
 * their own fields — so we have to sum to get the headline total. `cr` =
 * cache read, `cw` = cache write, `unc` = new uncached tokens. The
 * breakdown is dropped when no cache numbers were reported.
 */
const PromptTokenSuffix = ({ usage }: { usage?: InferenceUsage }) => {
  if (!hasPromptTokenData(usage)) return null;
  const { promptTokens, cacheReadTokens, cacheCreationTokens } = usage ?? {};
  const total = (promptTokens ?? 0) + (cacheReadTokens ?? 0) + (cacheCreationTokens ?? 0);

  const breakdown: string[] = [];
  if (cacheReadTokens !== undefined) breakdown.push(`${cacheReadTokens.toLocaleString()} cr`);
  if (cacheCreationTokens !== undefined)
    breakdown.push(`${cacheCreationTokens.toLocaleString()} cw`);
  if (
    promptTokens !== undefined &&
    (cacheReadTokens !== undefined || cacheCreationTokens !== undefined)
  ) {
    breakdown.push(`${promptTokens.toLocaleString()} unc`);
  }

  return (
    <span className={COUNT_SUFFIX_CLS}>
      {total.toLocaleString()} tok
      {breakdown.length > 0 && ` (${breakdown.join(", ")})`}
    </span>
  );
};

const rowKey = (label: string, index: number) => `${index}:${label}`;

export const InferenceInputSections = ({
  sections,
  usage,
  label = "model input",
  defaultOpen = true,
  roundCount,
  copyValue,
  showExpandAll = false,
}: {
  sections: InferenceInput;
  usage?: InferenceUsage;
  label?: string;
  defaultOpen?: boolean;
  roundCount?: number;
  /**
   * Override for the copy button (JSON-stringified). Pass this when `sections`
   * is a display-only projection and the raw underlying shape is more useful to
   * paste. Defaults to `sections`.
   */
  copyValue?: unknown;
  /**
   * Show an "Expand All" / "Collapse All" toggle that opens or closes every row
   * at once (used by the full-prompt section). When enabled the rows become
   * controlled; when off they stay uncontrolled via `DEFAULT_OPEN_SECTIONS`.
   */
  showExpandAll?: boolean;
}) => {
  const showRounds = roundCount !== undefined && roundCount > 1;

  // Control the parent collapsible so the toggle can be shown only while the
  // section is expanded. Behaviour-neutral for callers that don't opt in.
  const [open, setOpen] = useState(defaultOpen);

  // Controlled child-row open-state, seeded from the same defaults the
  // uncontrolled path uses. Only populated when the toggle is enabled.
  const [openRows, setOpenRows] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (showExpandAll) {
      sections.forEach((section, index) => {
        if (DEFAULT_OPEN_SECTIONS.has(section.label)) initial.add(rowKey(section.label, index));
      });
    }
    return initial;
  });

  // Check membership of every *current* row key rather than comparing counts:
  // that stays accurate even if `openRows` retains keys for rows no longer present.
  const allOpen =
    showExpandAll &&
    sections.length > 0 &&
    sections.every((section, index) => openRows.has(rowKey(section.label, index)));

  const toggleAll = () =>
    setOpenRows(allOpen ? new Set() : new Set(sections.map((s, i) => rowKey(s.label, i))));

  const setRowOpen = (key: string, isOpen: boolean) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(key);
      else next.delete(key);
      return next;
    });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SectionTrigger
        level="primary"
        label={label}
        suffix={
          (hasPromptTokenData(usage) || showRounds) && (
            <>
              <PromptTokenSuffix usage={usage} />
              {showRounds && <CountSuffix>{roundCount} rounds</CountSuffix>}
            </>
          )
        }
        copy={{
          getText: () => JSON.stringify(copyValue ?? sections, null, 2),
          label: `Copy ${label}`,
        }}
        rightContent={
          showExpandAll && open ? (
            <ExpandAllToggle allOpen={allOpen} onToggle={toggleAll} />
          ) : undefined
        }
      />
      <CollapsibleContent>
        <div className="space-y-1 mt-1 ml-2.5">
          {sections.map((section, index) => {
            // Labels can repeat across sections (e.g. flattened full-prompt
            // messages with the same role), so key by composite index + label.
            const key = rowKey(section.label, index);
            return (
              <CollapsibleField
                key={key}
                label={section.label}
                body={section.value}
                language={JSON_INPUT_FIELDS.has(section.label) ? "json" : undefined}
                {...(showExpandAll
                  ? { open: openRows.has(key), onOpenChange: (o: boolean) => setRowOpen(key, o) }
                  : { defaultOpen: DEFAULT_OPEN_SECTIONS.has(section.label) })}
                suffix={
                  <CountSuffix>~{estTokens(section.value).toLocaleString()} est tok</CountSuffix>
                }
                bodyMaxHeightClassName="max-h-48"
              />
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

/**
 * Flatten a `fullPrompt` chat log (rounds of messages) into a flat list of
 * labeled sections — one per message — so we can render it via the same
 * `InferenceInputSections` component. Multi-round forwards (AxGen tool-loops)
 * get a `round N · ` prefix on each label so the rendered output preserves
 * round boundaries. Assistant messages with thinking content split into two
 * sections (thinking + content).
 *
 * Two on-disk shapes are tolerated:
 *  - Current: `ChatLogMessage[][]` — one inner array per round.
 *  - Legacy: `Array<{model?, messages: ChatLogMessage[]}>` — pre-PR-4399
 *    inference events. Normalized to the current shape at read time so old
 *    debug events stay viewable without a backfill.
 */
const renderThinkingForDisplay = (m: FullPrompt[number][number]): string | null => {
  if (!m.thoughtBlocks?.length) return null;
  return m.thoughtBlocks.map((b) => (b.encrypted ? "[redacted thinking]" : b.data)).join("\n\n");
};

/**
 * Heuristic: surface a `· cache` label suffix when the captured message carries
 * a message-level `cache: true` flag. This reflects the actor chat path's
 * convention (the flag rides the envelope; Ax lands the Anthropic
 * `cache_control` marker on the trailing content block at serialization time).
 * It is not a ground-truth signal of what got cached — other capture paths
 * (e.g. AxGen's chat-log projection) don't carry the flag, so an absent label
 * doesn't mean the message was uncached.
 */
const isCachedMessage = (m: FullPrompt[number][number]): boolean => m["cache"] === true;

const flattenFullPrompt = (rounds: FullPrompt): InferenceInput => {
  const normalized: FullPrompt = rounds.map((round) => {
    if (Array.isArray(round)) return round;
    const legacy = round as { messages?: unknown };
    return Array.isArray(legacy.messages) ? (legacy.messages as FullPrompt[number]) : [];
  });
  return normalized.flatMap((round, i) =>
    round.flatMap((m) => {
      const roleLabel = m.name ? `${m.role}:${m.name}` : m.role;
      const prefix = normalized.length > 1 ? `round ${i + 1} · ` : "";
      const cacheSuffix = isCachedMessage(m) ? " · cache" : "";
      const sections: InferenceInput = [];
      const thinking = renderThinkingForDisplay(m);
      if (thinking !== null) {
        sections.push({ label: `${prefix}${roleLabel} · thinking`, value: thinking });
      }
      sections.push({
        label: `${prefix}${roleLabel}${cacheSuffix}`,
        value:
          typeof m.content === "string" ? m.content : (JSON.stringify(m.content, null, 2) ?? ""),
      });
      return sections;
    }),
  );
};

const ActionEventCard = ({ event }: { event: ActionThreadEvent }) => {
  const { content } = event;
  const durationMs = content.meta?.durationMs;
  const kindPrefix = ACTION_KIND_LABELS[content.kind];

  const bodySections: React.ReactNode[] = [];
  if (content.input) {
    const inputCode =
      typeof content.input === "string" ? content.input : JSON.stringify(content.input, null, 2);
    bodySections.push(<CodeBlock key="input" code={inputCode} language="json" label="Input" />);
  }
  if (content.kind === "execution") {
    bodySections.push(
      <CodeBlock key="code" code={content.code.source} language={content.code.language} />,
    );
  }

  return (
    <EventCard
      borderClassName="border-amber-700"
      header={
        <EventCardHeader
          label={`${kindPrefix}: ${content.name}`}
          labelClassName="text-amber-700"
          durationMs={durationMs}
          sortDate={event.sortDate}
          hasBody={bodySections.length > 0}
          isCommitted={event.committedAt !== null}
        />
      }
    >
      {bodySections.length > 0 && <div className="space-y-1.5">{bodySections}</div>}
    </EventCard>
  );
};

const OBSERVATION_STATE_LABELS: Record<
  NonNullable<ObservationThreadEvent["content"]["state"]>,
  string
> = {
  awaiting_user: "awaiting user",
  awaiting_external: "awaiting external",
  stopped: "stopped",
};

const unwrapSingleStringArray = (value: unknown): { value: unknown; unwrapped: boolean } => {
  if (Array.isArray(value) && value.length === 1 && typeof value[0] === "string") {
    return { value: value[0], unwrapped: true };
  }
  return { value, unwrapped: false };
};

const ObservationEventCard = ({ event }: { event: ObservationThreadEvent }) => {
  const { content } = event;
  const displayContent = unwrapSingleStringArray(content.content);
  const contentStr =
    typeof displayContent.value === "string"
      ? displayContent.value
      : JSON.stringify(displayContent.value, null, 2);
  const rawContentStr =
    typeof content.content === "string"
      ? content.content
      : JSON.stringify(content.content, null, 2);
  const stateLabel = content.state ? OBSERVATION_STATE_LABELS[content.state] : null;

  return (
    <EventCard
      borderClassName="border-slate-700"
      header={
        <EventCardHeader
          label={
            <>
              Observation: {content.name}{" "}
              {stateLabel ? (
                <Badge
                  size="xs"
                  className="border-teal-600 bg-white text-teal-700 px-1 py-0 text-[10px] leading-tight"
                >
                  {stateLabel}
                </Badge>
              ) : (
                content.isError && (
                  <Badge
                    size="xs"
                    className="border-red-600 bg-white text-red-700 px-1 py-0 text-[10px] leading-tight"
                  >
                    error
                  </Badge>
                )
              )}
            </>
          }
          labelClassName="text-slate-700"
          durationMs={content.meta?.durationMs}
          sortDate={event.sortDate}
          isCommitted
        />
      }
    >
      <div className="space-y-1.5">
        <CodeBlock
          code={contentStr}
          language={typeof displayContent.value === "string" ? "text" : "json"}
          label="Content"
          suffix={displayContent.unwrapped && <CountSuffix>unwrapped array</CountSuffix>}
          copyText={rawContentStr}
        />
        <ChangedInterpreterState
          changedVariables={content.meta?.changedVariables}
          changedWords={content.meta?.changedWords}
        />
      </div>
    </EventCard>
  );
};

/**
 * Collapsible disclosure for the per-turn variable/word diff carried on an
 * observation's `meta`. Renders nothing when both are empty so a typical
 * observation card stays uncluttered.
 */
const ChangedInterpreterState = ({
  changedVariables,
  changedWords,
}: {
  changedVariables?: Record<string, unknown>;
  changedWords?: Record<string, string>;
}) => {
  const variableCount = changedVariables ? Object.keys(changedVariables).length : 0;
  const wordCount = changedWords ? Object.keys(changedWords).length : 0;
  if (variableCount === 0 && wordCount === 0) return null;

  const variablesBody = variableCount > 0 ? JSON.stringify(changedVariables, null, 2) : "";
  const wordsBody = wordCount > 0 ? JSON.stringify(changedWords, null, 2) : "";
  const countParts: string[] = [];
  if (variableCount > 0) countParts.push(`${variableCount} var${variableCount === 1 ? "" : "s"}`);
  if (wordCount > 0) countParts.push(`${wordCount} word${wordCount === 1 ? "" : "s"}`);

  return (
    <Collapsible defaultOpen={false}>
      <SectionTrigger
        level="primary"
        label="interpreter state update"
        suffix={<CountSuffix>{countParts.join(", ")}</CountSuffix>}
      />
      <CollapsibleContent>
        <div className="space-y-1 mt-1 ml-2.5">
          {variableCount > 0 && (
            <CollapsibleField label="variables" body={variablesBody} language="json" defaultOpen />
          )}
          {wordCount > 0 && (
            <CollapsibleField label="words" body={wordsBody} language="json" defaultOpen />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ResourceEventCard = ({ event }: { event: ResourceThreadEvent }) => (
  <EventCard
    borderClassName="border-violet-200"
    header={
      <span className="font-medium font-mono text-2xs text-violet-700">
        Resource: {event.content.name}
      </span>
    }
  />
);

/**
 * Pill styling for debug events with level WARN+. Anything below WARN renders
 * no pill — the user-facing distinction is "did something noteworthy happen?".
 */
const DEBUG_LEVEL_PILL: Partial<Record<DebugThreadEvent["content"]["level"], string>> = {
  WARN: "border-yellow-600 bg-white text-yellow-700",
  ERROR: "border-red-600 bg-white text-red-700",
  FATAL: "border-red-700 bg-white text-red-800",
};

const DebugLevelPill = ({ level }: { level: DebugThreadEvent["content"]["level"] }) => {
  const cls = DEBUG_LEVEL_PILL[level];
  if (!cls) return null;
  return (
    <Badge size="xs" className={cn(cls, "px-1 py-0 text-[10px] leading-tight uppercase")}>
      {level.toLowerCase()}
    </Badge>
  );
};

const DebugEventCard = ({ event }: { event: DebugThreadEvent }) => {
  const { content } = event;
  const hasBody = content.description || (content.content && content.content.length > 0);

  return (
    <EventCard
      borderClassName="border-blue-700"
      defaultOpen={false}
      header={
        <EventCardHeader
          label={
            <>
              {content.name} <DebugLevelPill level={content.level} />
            </>
          }
          labelClassName="text-blue-700"
          durationMs={content.meta?.durationMs}
          sortDate={event.sortDate}
          hasBody={!!hasBody}
          isCommitted
        />
      }
    >
      {hasBody && (
        <div className="space-y-1.5">
          {content.description && (
            <div className="text-xs text-blue-700/80">{content.description}</div>
          )}
          {content.content?.map((item, i) => (
            <CodeBlock
              key={i}
              code={typeof item === "string" ? item : JSON.stringify(item, null, 2)}
              language={typeof item === "string" ? "text" : "json"}
              label="Data"
            />
          ))}
        </div>
      )}
    </EventCard>
  );
};

/**
 * Renders a context-compaction boundary — its stored notice, append-order
 * cutoff, and diagnostic meta — for inspection.
 */
export const CompactionEventCard = ({ event }: { event: CompactionThreadEvent }) => {
  const { content } = event;
  const isInvalidated = Boolean(content.invalidatedAt);
  // The append-order cutoff and strategy are top-level fields, not in `meta` —
  // surface them alongside the meta so the whole compaction record is inspectable
  // in the body rather than the id living only in the title.
  const diagnostics = {
    compactedThroughEventId: content.compactedThroughEventId,
    strategy: content.strategy,
    ...content.meta,
  };
  return (
    <EventCard
      borderClassName="border-pink-300"
      defaultOpen={false}
      header={
        <EventCardHeader
          label={
            <>
              <span className={cn(isInvalidated && "line-through")}>Context compaction</span>
              {content.meta?.reason ? (
                <span
                  className={cn(
                    "ml-1 font-mono text-2xs text-pink-700/70",
                    isInvalidated && "line-through",
                  )}
                >
                  {content.meta.reason}
                </span>
              ) : null}
              {isInvalidated ? (
                <span className="ml-1 text-muted-foreground">Invalidated</span>
              ) : null}
            </>
          }
          labelClassName="text-pink-700"
          sortDate={event.sortDate}
          isCommitted
        />
      }
    >
      <div className="space-y-1.5">
        <CodeBlock code={content.content} language="text" label="Notice" />
        <CodeBlock
          code={JSON.stringify(diagnostics, null, 2)}
          language="json"
          label="Diagnostics"
        />
      </div>
    </EventCard>
  );
};

const INFERENCE_AUTO_COLLAPSE_FIELDS: Record<string, readonly string[]> = {
  actor: ["forthicCode", "assistantMessage"],
  responder: ["assistantMessage"],
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const traceViewerUrl = (traceId: string, spanId?: string) => {
  if (import.meta.env.DEV) {
    return `http://localhost:16686/trace/${traceId}${spanId ? `?uiFind=${spanId}` : ""}`;
  }
  const span = spanId ? `;spanId=${spanId}` : "";
  return `https://console.cloud.google.com/traces/explorer;traceId=${traceId}${span}?project=${getProjectId()}`;
};

/**
 * Trace deep-link for an inference event, rendered in the model-output footer.
 * Links to the trace that contains this LLM call (the Ax model-call span is a child
 * of it). `spanId` is baked into the URL to focus the span, not shown as text.
 */
const TraceLink = ({ traceId, spanId }: { traceId?: string; spanId?: string }) => {
  if (!traceId) return null;
  return (
    <a
      href={traceViewerUrl(traceId, spanId)}
      target="_blank"
      rel="noopener noreferrer"
      className="underline hover:text-foreground"
      onClick={(e) => e.stopPropagation()}
    >
      trace
    </a>
  );
};

// Claude console Logs page. Static — the console has no documented per-request
// deep-link, so we copy the id and point the user here to paste it.
const CLAUDE_LOGS_URL = "https://platform.claude.com/workspaces/default/logs";

/**
 * Provider request ids from the model response, with a copy button. Copying an
 * Anthropic id (`req_…`) confirms with a toast linking to the Claude Logs page,
 * where the id can be pasted (the console has no per-request deep-link). Gemini
 * ids copy without the link — they have no Claude console.
 */
const ProviderRequestIds = ({ ids }: { ids: string[] }) => {
  const text = ids.join(", ");
  const hasAnthropic = ids.some((id) => id.startsWith("req_"));
  return (
    <span className="inline-flex items-center gap-1">
      <span>{text}</span>
      <CopyButton
        getText={() => text}
        label="Copy request id"
        onCopied={() =>
          confirmationToast(
            "Request ID copied",
            { duration: 6000 },
            hasAnthropic
              ? {
                  description: (
                    <>
                      <a
                        href={CLAUDE_LOGS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Anthropic request logs
                      </a>{" "}
                      (cmd+f the requestId)
                    </>
                  ),
                }
              : undefined,
          )
        }
      />
    </span>
  );
};

const InferenceOutputBlock = ({
  name,
  output,
  usage,
  providerRequestIds,
  traceId,
  spanId,
}: {
  name: string;
  output: unknown;
  usage?: InferenceUsage;
  providerRequestIds?: string[];
  traceId?: string;
  spanId?: string;
}) => {
  const collapseSet = useMemo(() => new Set(INFERENCE_AUTO_COLLAPSE_FIELDS[name] ?? []), [name]);
  const isPlain = isPlainObject(output);
  const topLevelCopyText = useMemo(() => {
    if (output === undefined || output === null) return "";
    return isPlain
      ? JSON.stringify(output, null, 2)
      : typeof output === "string"
        ? output
        : JSON.stringify(output, null, 2);
  }, [isPlain, output]);

  const outputEntries = useMemo(
    () =>
      isPlain
        ? Object.entries(output).map(([fieldName, value]) => ({
            fieldName,
            body: typeof value === "string" ? value : JSON.stringify(value, null, 2),
          }))
        : [],
    [isPlain, output],
  );

  if (output === undefined || output === null) return null;
  if (isPlain && outputEntries.length === 0) return null;

  const usageParts: string[] = [];
  if (usage?.completionTokens !== undefined)
    usageParts.push(`${usage.completionTokens.toLocaleString()} tok`);

  const inner = isPlain ? (
    <div className="space-y-1 mt-1 ml-2.5">
      {outputEntries.map(({ fieldName, body }) => (
        <CollapsibleField
          key={fieldName}
          label={fieldName}
          body={body}
          language={fieldName === "forthicCode" ? "forthic" : undefined}
          defaultOpen={!collapseSet.has(fieldName)}
        />
      ))}
    </div>
  ) : (
    <CollapsibleFieldContent className="max-h-64 mt-1 ml-2.5">
      {typeof output === "string" ? output : JSON.stringify(output, null, 2)}
    </CollapsibleFieldContent>
  );

  return (
    <Collapsible defaultOpen={false}>
      <SectionTrigger
        level="primary"
        label="model output"
        suffix={usageParts.length > 0 && <CountSuffix>{usageParts.join(", ")}</CountSuffix>}
        rightContent={
          (traceId || (providerRequestIds && providerRequestIds.length > 0)) && (
            // This lives inside the CollapsibleTrigger button, which otherwise eats
            // drag-to-select and toggles the row on click. Stop both so the req id is
            // selectable and clicking the trace link doesn't collapse the section.
            <span
              className="flex items-center gap-1 text-2xs text-muted-foreground/70 font-mono select-text cursor-auto"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <TraceLink traceId={traceId} spanId={spanId} />
              {traceId && providerRequestIds && providerRequestIds.length > 0 && (
                <span aria-hidden>·</span>
              )}
              {providerRequestIds && providerRequestIds.length > 0 && (
                <ProviderRequestIds ids={providerRequestIds} />
              )}
            </span>
          )
        }
        copy={{ getText: () => topLevelCopyText, label: "Copy model output" }}
      />
      <CollapsibleContent>{inner}</CollapsibleContent>
    </Collapsible>
  );
};

/**
 * Non-collapsible "full prompt" row rendered when the inference event carries
 * prompt/cache token counts but no captured `fullPrompt` content (e.g. call
 * sites that don't opt into `captureFullPrompt`). Mirrors the no-text branch
 * of `ThoughtRow`.
 */
const FullPromptTokensRow = ({ usage }: { usage?: InferenceUsage }) => (
  <div className={ROW_CLS}>
    <Circle className="size-1.5 shrink-0 mx-[3px] stroke-[2.5]" />
    <span className={TRIGGER_LABEL_CLS}>full prompt</span>
    <PromptTokenSuffix usage={usage} />
  </div>
);

const ThoughtRow = ({ thought, usage }: { thought?: string; usage?: InferenceUsage }) => {
  const tokenParts = [
    usage?.thoughtsTokens !== undefined && `${usage.thoughtsTokens.toLocaleString()} tok`,
    usage?.reasoningTokens !== undefined && `${usage.reasoningTokens.toLocaleString()} tok`,
  ].filter(Boolean);
  const tokenSuffix =
    tokenParts.length > 0 ? <CountSuffix>{tokenParts.join(", ")}</CountSuffix> : null;

  // No thought text — render a non-collapsible row with just the token count.
  if (!thought) {
    return (
      <div className={ROW_CLS}>
        <Circle className="size-1.5 shrink-0 mx-[3px] stroke-[2.5]" />
        <span className={TRIGGER_LABEL_CLS}>thought</span>
        {tokenSuffix}
      </div>
    );
  }

  return (
    <Collapsible defaultOpen>
      <SectionTrigger
        level="primary"
        label="thought"
        suffix={tokenSuffix}
        copy={{ getText: () => thought, label: "Copy thought" }}
      />
      <CollapsibleContent>
        <div className="max-h-64 overflow-y-auto rounded border border-border bg-muted/30 px-1.5 mt-0.5 ml-4">
          <Markdown
            className={cn(
              "max-w-none text-xs leading-5 text-muted-foreground",
              "prose prose-sm prose-zinc",
              "prose-p:my-1",
              "prose-headings:mt-2 prose-headings:mb-1 prose-headings:font-semibold prose-headings:text-foreground/80",
              "prose-h1:text-base prose-h2:text-sm prose-h3:text-xs",
              "prose-strong:font-semibold prose-strong:text-foreground/70",
              "prose-code:rounded prose-code:bg-muted/60 prose-code:text-muted-foreground prose-code:px-1 prose-code:py-0.5 prose-code:font-normal",
              "[&_code:not(pre_code)]:!text-[0.85em]",
              "prose-code:before:content-none prose-code:after:content-none",
            )}
          >
            {thought}
          </Markdown>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

/** Streaming-experiment timing breakdown: Wait / Thinking / Response / Total. */
const TimingBreakdownTooltip = ({
  durationMs,
  ttftMs,
  thinkingMs,
  responseMs,
  responseStreamableMs,
}: {
  durationMs?: number;
  ttftMs?: number;
  thinkingMs?: number;
  responseMs?: number;
  responseStreamableMs?: number;
}) => {
  // Legacy/non-streamed events only carry `durationMs` — the `?` icon would
  // reveal nothing the header doesn't already show, so suppress unless at
  // least one phase timestamp made it through.
  if (ttftMs === undefined && thinkingMs === undefined && responseMs === undefined) {
    return null;
  }

  const phases: { label: string; value?: number; nested?: boolean }[] = [
    { label: "Wait", value: ttftMs },
    // Skip when zero — non-thinking models always land here, and a "0ms"
    // row in the timeline is just noise.
    ...(thinkingMs !== undefined && thinkingMs > 0
      ? [{ label: "Thinking", value: thinkingMs }]
      : []),
    { label: "Response", value: responseMs },
    // The streamable slice of the response — only on message-producing turns
    // where the span was located. Rendered as a sub-row of Response (↳). Skip
    // when zero/absent (scaffolding turns).
    ...(responseStreamableMs !== undefined
      ? [{ label: "Streamable", value: responseStreamableMs, nested: true }]
      : []),
  ];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Timing breakdown"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          className="relative inline-flex items-center align-middle text-muted-foreground/60 hover:text-foreground transition-colors cursor-help"
        >
          <CircleHelp className="size-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="px-2.5 py-1.5">
        <table className="text-2xs font-mono tabular-nums">
          <tbody>
            {phases.map(({ label, value, nested }) => (
              <tr key={label}>
                <td className="pr-4 text-muted-foreground">
                  {nested && <span className="text-muted-foreground/60">↳ </span>}
                  {label}
                </td>
                <td className="text-right">
                  {value === undefined ? "—" : formatDurationMs(value)}
                </td>
              </tr>
            ))}
            <tr className="border-t border-border/40">
              <td className="pr-4 pt-0.5 font-medium">Total</td>
              <td className="pt-0.5 text-right font-medium">
                {durationMs === undefined ? "—" : formatDurationMs(durationMs)}
              </td>
            </tr>
          </tbody>
        </table>
      </TooltipContent>
    </Tooltip>
  );
};

const InferenceEventCard = ({ event }: { event: InferenceThreadEvent }) => {
  const { content } = event;
  const { input, fullPrompt, thought, output, meta } = content;

  const hasInferenceInput = !!input && input.length > 0;
  const hasFullPrompt = !!fullPrompt && fullPrompt.length > 0;
  const hasPromptTokens =
    meta?.usage?.promptTokens !== undefined ||
    meta?.usage?.cacheReadTokens !== undefined ||
    meta?.usage?.cacheCreationTokens !== undefined;
  const hasThought = !!thought && thought.length > 0;
  const hasThoughtTokens =
    meta?.usage?.thoughtsTokens !== undefined || meta?.usage?.reasoningTokens !== undefined;
  const hasInferenceOutput =
    output !== undefined &&
    output !== null &&
    !(isPlainObject(output) && Object.keys(output).length === 0);
  const fullPromptSections = useMemo(
    () => (hasFullPrompt ? flattenFullPrompt(fullPrompt) : []),
    [fullPrompt, hasFullPrompt],
  );
  const hasBody =
    hasInferenceInput ||
    hasFullPrompt ||
    hasPromptTokens ||
    hasThought ||
    hasThoughtTokens ||
    hasInferenceOutput;

  return (
    <EventCard
      borderClassName="border-green-700"
      defaultOpen
      header={
        <EventCardHeader
          label={
            <>
              Inference: {content.name}
              {meta?.model && (
                <span className="ml-1.5 font-normal text-muted-foreground">{meta.model}</span>
              )}
              {(meta?.effort || meta?.thinkingTokenBudget) && (
                <span className="ml-1.5 font-normal text-muted-foreground/70">
                  ({meta.effort ?? meta.thinkingTokenBudget})
                </span>
              )}
            </>
          }
          labelClassName="text-green-700"
          durationMs={meta?.durationMs}
          extraTiming={
            <TimingBreakdownTooltip
              durationMs={meta?.durationMs}
              ttftMs={meta?.ttftMs}
              thinkingMs={meta?.thinkingMs}
              responseMs={meta?.responseMs}
              responseStreamableMs={meta?.responseStreamableMs}
            />
          }
          sortDate={event.sortDate}
          hasBody={hasBody}
          isCommitted={event.committedAt !== null}
        />
      }
    >
      {hasBody && (
        <div className="space-y-1.5">
          {hasInferenceInput && <InferenceInputSections sections={input} defaultOpen={false} />}
          {hasFullPrompt ? (
            <InferenceInputSections
              sections={fullPromptSections}
              usage={meta?.usage}
              label="full prompt"
              defaultOpen={false}
              showExpandAll
              roundCount={fullPrompt.length}
              // Single-round inferences (e.g. the actor's direct chat call) unwrap to a flat
              // `[msg, msg, ...]` for a cleaner paste; multi-round AxGen tool-loops keep the
              // `[[round1], [round2]]` shape so round boundaries survive the copy.
              copyValue={
                fullPrompt.length === 1 && Array.isArray(fullPrompt[0]) ? fullPrompt[0] : fullPrompt
              }
            />
          ) : (
            hasPromptTokens && <FullPromptTokensRow usage={meta?.usage} />
          )}
          {(hasThought || hasThoughtTokens) && (
            <ThoughtRow thought={hasThought ? thought : undefined} usage={meta?.usage} />
          )}
          <InferenceOutputBlock
            name={content.name}
            output={output}
            usage={meta?.usage}
            providerRequestIds={meta?.providerRequestIds}
            traceId={meta?.traceId}
            spanId={meta?.spanId}
          />
        </div>
      )}
    </EventCard>
  );
};

const ChildClusterLabel = ({ items }: { items: ChildClusterItem[] }) => {
  const subturnCount = items.filter((i) => i.kind === "subturn").length;
  const debugCount = items.length - subturnCount;
  return (
    <span className="flex items-center gap-1">
      {subturnCount > 0 && (
        <span className="text-amber-700">
          {subturnCount} turn{subturnCount === 1 ? "" : "s"}
        </span>
      )}
      {subturnCount > 0 && debugCount > 0 && <span>,</span>}
      {debugCount > 0 && (
        <span className="text-blue-700">
          {debugCount} debug event{debugCount === 1 ? "" : "s"}
        </span>
      )}
    </span>
  );
};

const clusterDefaultOpen = (depth: number, autoExpandMode: AutoExpandMode): boolean => {
  switch (autoExpandMode) {
    case "minimal":
      // This isn't just "false" because it's a little annoying to click to open a cluster and then have to click again to open its children.
      // So we close 0 (top level), but if you manually open it, you see it + its children
      return depth === 1;
    case "expanded":
      // Only show the top level one - 1 click to see the children.
      // This is closest to what the top level agent sees
      return depth === 0;
    case "all":
      return true;
    default:
      throw new UnsupportedValueError(autoExpandMode);
  }
};

const ChildClusterCollapsible = ({
  items,
  depth = 0,
}: {
  items: ChildClusterItem[];
  /** Cluster nesting depth: 0 = top-level, 1 = direct descendants, 2+ = deeper. */
  depth?: number;
}) => {
  const mode = useAtomValue(autoExpandAtom);
  const [open, setOpen] = useState(() => clusterDefaultOpen(depth, mode));
  // Snap to the new mode's default whenever the user cycles.
  useEffect(() => {
    setOpen(clusterDefaultOpen(depth, mode));
  }, [depth, mode]);

  if (items.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="CollapsibleTrigger flex items-center gap-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer rounded hover:bg-muted/50 w-full">
        <ChevronDown className="size-3 shrink-0 transition-transform CollapsibleTriggerRightIcon" />
        <ChildClusterLabel items={items} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1 pt-1 pb-1">
          {items.map((item) =>
            item.kind === "subturn" ? (
              <TurnContainer key={childClusterItemKey(item)} node={item.node} depth={depth} />
            ) : (
              <EventContent key={childClusterItemKey(item)} event={item.event} />
            ),
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// Core rendering
// ---------------------------------------------------------------------------

// memo here: unchanged event objects keep their references when sibling events update.
const EventContent = React.memo(function EventContent({ event }: { event: NonMessageThreadEvent }) {
  switch (event.type) {
    case "action":
      return <ActionEventCard event={event} />;
    case "observation":
      return <ObservationEventCard event={event} />;
    case "resource":
      return <ResourceEventCard event={event} />;
    case "inference":
      return <InferenceEventCard event={event} />;
    case "debug":
      return <DebugEventCard event={event} />;
    case "compaction":
      return <CompactionEventCard event={event} />;
    default:
      warnUnsupportedValue(event);
      return null;
  }
});

const TurnContainer = ({ node, depth = 0 }: { node: TurnNode; depth?: number }) => {
  const segments = useMemo(() => buildTurnSegments(node), [node]);

  if (segments.length === 0) return null;

  return (
    <div className="pb-2">
      {segments.map((segment, index) => {
        const key =
          segment.kind === "primary"
            ? `event-${segment.event.id}`
            : `cluster-${segment.items[0] ? childClusterItemKey(segment.items[0]) : index}`;

        const prev = segments[index - 1];
        const prevHasTrailingGutter = prev?.kind === "cluster";
        const curHasLeadingGutter = segment.kind === "cluster";
        const showConnector = index > 0 && !prevHasTrailingGutter && !curHasLeadingGutter;

        return (
          <React.Fragment key={key}>
            {showConnector && <div className="border-l border-border ml-3 h-2" aria-hidden />}
            {segment.kind === "primary" ? (
              <EventContent event={segment.event} />
            ) : (
              <div className="border-l border-border ml-3 pl-3 py-1">
                <ChildClusterCollapsible items={segment.items} depth={depth + 1} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const RootItemRenderer = ({
  item,
}: {
  item: TurnRootItem | ChildClusterRootItem | OrphanRootItem;
}) => {
  if (item.kind === "turn") {
    return <TurnContainer node={item.node} />;
  }
  if (item.kind === "cluster") {
    return (
      <div className="pb-2">
        <ChildClusterCollapsible items={item.items} depth={0} />
      </div>
    );
  }
  return (
    <div className="pb-2">
      <EventContent event={item.event} />
    </div>
  );
};
